import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  // MenuItem,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  Fade,
} from "@mui/material";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import { toast } from "react-toastify";
import { signUp } from "../actions/userActions";
import { phone } from "phone";
import SignUpSuccessDialog from "../dialogs/SignUpSuccessDialog";
import CorporateDialog from "../dialogs/CorporateDialog";
import TermsDialog from "../dialogs/TermsDialog";
import PrivacyPolicyDialog from "../dialogs/PrivacyPolicyDialog";
import {
  ModernTextField,
  ModernButton,
  GradientBackground,
  AuthCard,
} from "../components/UI/ModernComponents";

export default function SignUpForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userSignup = useSelector((state) => state.userSignup);
  const { loading, message, errorMessage, status } = userSignup;

  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isCorporateDialogOpen, setCorporateDialogOpen] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const [openPrivacyPolicyDialog, setOpenPrivacyPolicyDialog] = useState(false);

  const validatePhoneNumber = (inputNumber) => {
    if (!inputNumber) return "Phone Number is required";
    const { isValid } = phone(inputNumber, { country: "pk" });
    return isValid ? true : "Invalid phone number format";
  };

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const password = watch("password", "");

  const onSubmit = async (data) => {
    const { fullName, email, phoneNumber, password } = data;
    try {
      dispatch(
        signUp(
          fullName,
          email,
          phoneNumber,
          password,
          // cnic,
          // userType,
          // organization,
          // designation,
          // officeAddress,
          // barCouncil
        ),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const showPopup = () => {
    setDialogOpen(true);
  };

  const closePopup = () => {
    setDialogOpen(false);
    navigate("/");
  };

  const showCorporatePopup = () => {
    setCorporateDialogOpen(true);
  };

  const closeCorporatePopup = () => {
    setCorporateDialogOpen(false);
    navigate("/");
  };

  useEffect(() => {
    if (errorMessage) {
      if (errorMessage === "Email already exists.") {
        setError("email", {
          type: "manual",
          message: "Email already exists.",
        });
      } else if (errorMessage === "Phone number already exists.") {
        setError("phoneNumber", {
          type: "manual",
          message: "Phone number already exists.",
        });
      }
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
      dispatch({ type: "USER_SIGNUP_RESET" });
    }
    if (message) {
      if (status === 200) {
        showCorporatePopup();
      } else {
        showPopup();
      }
      dispatch({ type: "USER_SIGNUP_RESET" });
    }
  }, [message, errorMessage, status, dispatch, setError]);

  return (
    <>
      <Helmet>
        <title>LawGPT - Create Account</title>
      </Helmet>
      <GradientBackground>
        {!isCorporateDialogOpen ? (
          <Fade in timeout={800}>
            <AuthCard elevation={0}>
              {/* Header */}
              <Box sx={{ textAlign: "center", mb: 3 }}>
                {/* Logo */}
                <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                  <img
                    src="/images/legalSearchLogo2.png"
                    alt="LawGPT Logo"
                    style={{
                      width: "180px",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "24px", sm: "28px" },
                    color: "#12243C",
                    mb: 1,
                    fontFamily: "var(--font-sans)",
                    background:
                      "linear-gradient(135deg, #12243C 0%, #0073A6 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Create your account
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#6B7280",
                    fontSize: "14px",
                    fontWeight: 400,
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  Join LawGPT and transform your legal practice
                </Typography>
              </Box>

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <ModernTextField
                  fullWidth
                  label="Full Name"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon
                          sx={{ color: "#9CA3AF", fontSize: "20px" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  {...register("fullName", {
                    required: "Full name is required",
                    minLength: {
                      value: 2,
                      message: "Name must be at least 2 characters",
                    },
                  })}
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "12px",
                      marginLeft: "4px",
                      color: "#EF4444",
                      fontFamily:
                        "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                    },
                  }}
                />

                <ModernTextField
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  type="email"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon
                          sx={{ color: "#9CA3AF", fontSize: "20px" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "12px",
                      marginLeft: "4px",
                      color: "#EF4444",
                      fontFamily:
                        "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                    },
                  }}
                />

                <ModernTextField
                  fullWidth
                  label="Phone Number"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon
                          sx={{ color: "#9CA3AF", fontSize: "20px" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  {...register("phoneNumber", {
                    required: "Phone number is required",
                    validate: validatePhoneNumber,
                  })}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "12px",
                      marginLeft: "4px",
                      color: "#EF4444",
                      fontFamily:
                        "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                    },
                  }}
                />

                <ModernTextField
                  fullWidth
                  label="Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon
                          sx={{ color: "#9CA3AF", fontSize: "20px" }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: "#9CA3AF",
                            padding: "4px",
                            "&:hover": {
                              color: "#6B7280",
                            },
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOffOutlined />
                          ) : (
                            <VisibilityOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message:
                        "Password must contain uppercase, lowercase, and number",
                    },
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "12px",
                      marginLeft: "4px",
                      color: "#EF4444",
                      fontFamily:
                        "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                    },
                  }}
                />

                <ModernTextField
                  fullWidth
                  label="Confirm Password"
                  variant="outlined"
                  type={showConfirmPassword ? "text" : "password"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon
                          sx={{ color: "#9CA3AF", fontSize: "20px" }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          sx={{
                            color: "#9CA3AF",
                            padding: "4px",
                            "&:hover": {
                              color: "#6B7280",
                            },
                          }}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffOutlined />
                          ) : (
                            <VisibilityOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (value) =>
                      value === password || "Passwords do not match",
                  })}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "12px",
                      marginLeft: "4px",
                      color: "#EF4444",
                      fontFamily:
                        "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                    },
                  }}
                />

                {/* Terms and Privacy */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register("terms", {
                          required: "You must accept the terms and conditions",
                        })}
                        sx={{
                          color: "#0073A6",
                          "&.Mui-checked": {
                            color: "#00C4F0",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6B7280",
                          fontSize: "13px",
                          fontFamily: "var(--font-sans)",
                        }}
                      >
                        I agree to the{" "}
                        <Typography
                          component="span"
                          onClick={() => setOpenTermsDialog(true)}
                          sx={{
                            cursor: "pointer",
                            color: "#0073A6",
                            fontWeight: 600,
                            fontSize: "13px",
                            fontFamily: "var(--font-sans)",
                            "&:hover": {
                              color: "#00C4F0",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Terms of Service
                        </Typography>{" "}
                        and{" "}
                        <Typography
                          component="span"
                          onClick={() => setOpenPrivacyPolicyDialog(true)}
                          sx={{
                            cursor: "pointer",
                            color: "#0073A6",
                            fontWeight: 600,
                            fontSize: "13px",
                            fontFamily: "var(--font-sans)",
                            "&:hover": {
                              color: "#00C4F0",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Privacy Policy
                        </Typography>
                      </Typography>
                    }
                  />
                  {errors.terms && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#EF4444",
                        fontSize: "12px",
                        ml: 4,
                        display: "block",
                        fontFamily: "var(--font-sans)",
                      }}
                    >
                      {errors.terms.message}
                    </Typography>
                  )}
                </Box>

                {/* Sign Up Button */}
                <ModernButton
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    backgroundColor: "#0073A6",
                    "&:hover": {
                      backgroundColor: "#005A8A",
                    },
                    "&:disabled": {
                      backgroundColor: "#E5E7EB",
                      color: "#9CA3AF",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
                  ) : (
                    "Create Account"
                  )}
                </ModernButton>
              </Box>

              {/* Divider */}
              <Divider sx={{ my: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#9CA3AF",
                    px: 2,
                    fontSize: "13px",
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  or
                </Typography>
              </Divider>

              {/* Login Link */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6B7280",
                    fontSize: "13px",
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  Already have an account?{" "}
                  <Typography
                    component="span"
                    onClick={() => navigate("/")}
                    sx={{
                      cursor: "pointer",
                      color: "#0073A6",
                      fontWeight: 600,
                      fontSize: "13px",
                      fontFamily: "var(--font-sans)",
                      "&:hover": {
                        color: "#00C4F0",
                        textDecoration: "underline",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    Sign in
                  </Typography>
                </Typography>
              </Box>
            </AuthCard>
          </Fade>
        ) : (
          <CorporateDialog
            isCorporateDialogOpen={isCorporateDialogOpen}
            closeCorporatePopup={closeCorporatePopup}
          />
        )}

        <SignUpSuccessDialog open={isDialogOpen} onClose={closePopup} />

        {/* Terms Dialog */}
        <TermsDialog
          open={openTermsDialog}
          onClose={() => setOpenTermsDialog(false)}
        />

        {/* Privacy Policy Dialog */}
        <PrivacyPolicyDialog
          open={openPrivacyPolicyDialog}
          onClose={() => setOpenPrivacyPolicyDialog(false)}
        />
      </GradientBackground>
    </>
  );
}
